import trackingEventDictionary from "@core/dictionary/tracking-event";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { getUserActivePlan } from "@helpers/profileHelper";
import { captureException } from "@sentry/react";
import AddFavModalUntyped from "@shared/AddFavModal";
import { getApiUrl } from "@utils/baseUrl";
import { ERROR_MSG } from "@utils/enum";
import { getIntelUrl } from "@utils/intelUrl";
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { getIntelHubApiUrl } from "./api";
import PersonaTabs from "./components/PersonaTabs";
import RecommendationsTable from "./components/RecommendationsTable";
import RecommendationsToolbar from "./components/RecommendationsToolbar";
import { BOOKMARK_EVENT, COMPANY_VIEW_EVENT, DISMISS_EVENT, EMPTY_STATE_TEXT, IMP_EXP_PERSONAS, PERSONA_TABS } from "./constants";
import useRecommendations from "./hooks/useRecommendations";
import type { BookmarkType, RecommendationRow } from "./types";

const AddFavModal = AddFavModalUntyped as unknown as (props: Record<string, unknown>) => ReactElement;

type RecommendationsViewProps = {
  lineOfBusiness: string;
  refreshKey: number;
  onModifyClick: () => void;
};

const RecommendationsView = ({ lineOfBusiness, refreshKey, onModifyClick }: RecommendationsViewProps) => {
  const { pathname, search } = useLocation();
  const urlParams = useMemo(() => new URLSearchParams(search), [search]);
  const isImpExp = IMP_EXP_PERSONAS.includes(lineOfBusiness);
  const personaTabs = useMemo(() => (isImpExp ? PERSONA_TABS.filter(({ lobs }) => lobs.includes(lineOfBusiness)) : []), [isImpExp, lineOfBusiness]);
  const [activeTab, setActiveTab] = useState<BookmarkType | null>(personaTabs[0]?.key ?? null);
  const [selected, setSelected] = useState<RecommendationRow[]>([]);
  const [clearSelection, setClearSelection] = useState(false);
  const [rowsToBookmark, setRowsToBookmark] = useState<RecommendationRow[] | null>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);

  const { rows, loading, refetch } = useRecommendations({ lineOfBusiness, isImpExp, bookmarkType: activeTab, refreshKey });
  const eventKey = isImpExp && activeTab ? activeTab : "bd";

  // One-shot flag for ReactTableTrademo: true clears its internal selection, then resets.
  useEffect(() => {
    if (!clearSelection) return undefined;
    const timer = setTimeout(() => setClearSelection(false), 0);
    return () => clearTimeout(timer);
  }, [clearSelection]);

  const resetSelection = useCallback(() => {
    setSelected([]);
    setClearSelection(true);
  }, []);

  // The table only reports an empty selection once it remounts with rows, so clear it here while
  // loading or when the new list is empty; otherwise "Bookmark selected" keeps the old rows.
  useEffect(() => {
    setSelected([]);
  }, [rows, loading]);

  const trackEvent = useCallback(
    (eventName: string, extra: Record<string, unknown> = {}) => {
      if (!eventName) return;
      trademoTrackingEvents(eventName, { ...generateTrackingObject("intel-hub", pathname, getUserActivePlan()), ...extra });
    },
    [pathname]
  );

  const trackRowEvent = useCallback((eventName: string, row: RecommendationRow) => trackEvent(eventName, { companyId: row.id, companyName: row.companyName }), [trackEvent]);

  const handleTabChange = (tab: BookmarkType) => {
    trackEvent(trackingEventDictionary.intelHubTabClick, { tab });
    resetSelection();
    setActiveTab(tab);
  };

  const handleCompanyClick = useCallback(
    async (row: RecommendationRow) => {
      trackRowEvent(COMPANY_VIEW_EVENT[eventKey], row);
      try {
        const { data } = await axios.get(getApiUrl("companyProfiles/getSlug"), { params: { companyId: row.id } });
        if (data?.status === "success" && data?.data?.companySlug) {
          const url = `${getIntelUrl()}${data.data.companySlug}/${data.data.aliasCompanyId}?country=${urlParams.get("country") ?? "multicountry"}&type=${urlParams.get("type") ?? "import"}&tab=I`;
          window.open(url, "_blank", "noopener,noreferrer");
        } else {
          toast.error("No company profile found in our system !!!");
        }
      } catch (error) {
        captureException(error);
        toast.error(ERROR_MSG);
      }
    },
    [eventKey, trackRowEvent, urlParams]
  );

  const handleDismiss = useCallback(
    async (row: RecommendationRow) => {
      setDismissingId(row.id);
      try {
        const { data } = await axios.post(getIntelHubApiUrl("recommendations/dismiss"), { company_id: row.id });
        if (data?.success === true) {
          toast.success("Suggestion dismissed");
          trackRowEvent(DISMISS_EVENT[eventKey], row);
          refetch();
        }
      } catch (error) {
        captureException(error);
        toast.error("Failed to dismiss suggestion");
      } finally {
        setDismissingId(null);
      }
    },
    [eventKey, refetch, trackRowEvent]
  );

  const handleBookmark = useCallback((row: RecommendationRow) => setRowsToBookmark([row]), []);

  const handleBookmarkSuccess = () => {
    (rowsToBookmark || []).forEach((row) => trackRowEvent(BOOKMARK_EVENT[eventKey], row));
    resetSelection();
    refetch();
  };

  const emptyText = isImpExp ? EMPTY_STATE_TEXT.hscode : EMPTY_STATE_TEXT.bd;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <PersonaTabs tabs={personaTabs} activeTab={activeTab} onChange={handleTabChange} />
      <RecommendationsToolbar selectedCount={selected.length} onModifyClick={onModifyClick} onBulkBookmark={() => setRowsToBookmark(selected)} onClearSelection={resetSelection} />
      <RecommendationsTable rows={rows} loading={loading} clearSelection={clearSelection} onSelectionChange={setSelected} onCompanyClick={handleCompanyClick} onBookmark={handleBookmark} onDismiss={handleDismiss} dismissingId={dismissingId} emptyText={emptyText} showSignal={!isImpExp} />
      {rowsToBookmark && <AddFavModal profileType="company" show handleClose={() => setRowsToBookmark(null)} prospectData={rowsToBookmark.map((row) => row.raw)} tradeDirection={urlParams.get("type")} dataSource={urlParams.get("country")} defaultBookmarkType={activeTab} onAddSuccess={handleBookmarkSuccess} />}
    </div>
  );
};

export default RecommendationsView;
